import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';

type FamilyPreset = 'elderly-care' | 'co-parenting' | 'family-children' | 'custom';

const PRESETS: Record<FamilyPreset, { hasMedications: boolean; hasCustody: boolean; hasActivities: boolean; hasEmergency: boolean }> = {
  'elderly-care': { hasMedications: true, hasCustody: false, hasActivities: false, hasEmergency: true },
  'co-parenting': { hasMedications: false, hasCustody: true, hasActivities: true, hasEmergency: false },
  'family-children': { hasMedications: false, hasCustody: false, hasActivities: true, hasEmergency: false },
  'custom': { hasMedications: true, hasCustody: false, hasActivities: false, hasEmergency: true },
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [familyName, setFamilyName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<FamilyPreset | null>(null);
  const [hasMedications, setHasMedications] = useState(true);
  const [hasCustody, setHasCustody] = useState(false);
  const [hasActivities, setHasActivities] = useState(false);
  const [hasEmergency, setHasEmergency] = useState(true);
  const [dependentName, setDependentName] = useState('');
  const [dependentDob, setDependentDob] = useState('');
  const [dependentNotes, setDependentNotes] = useState('');
  const [error, setError] = useState('');
  const [planError, setPlanError] = useState(false);
  const [loading, setLoading] = useState(false);

  const applyPreset = (preset: FamilyPreset) => {
    setSelectedPreset(preset);
    const flags = PRESETS[preset];
    setHasMedications(flags.hasMedications);
    setHasCustody(flags.hasCustody);
    setHasActivities(flags.hasActivities);
    setHasEmergency(flags.hasEmergency);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setPlanError(false);
    setLoading(true);

    try {
      await api.createFamily({
        name: familyName,
        dependentName,
        dependentDob: dependentDob || undefined,
        dependentNotes: dependentNotes || undefined,
        hasMedications,
        hasCustody,
        hasActivities,
        hasEmergency,
      });

      const allFamilies = await api.getFamilies().catch(() => []);
      navigate(allFamilies.length > 1 ? '/home' : '/dashboard');
    } catch (err: any) {
      if (err.status === 403) {
        setPlanError(true);
      } else {
        setError(err.message || 'Failed to create family');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    if (step === 1 && familyName) {
      setStep(2);
    } else if (step === 2 && selectedPreset) {
      setStep(3);
    }
  };

  const dependentLabel = hasCustody || hasActivities
    ? 'Add your child'
    : 'Who are you caring for?';

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center font-fraunces text-3xl font-light tracking-tight text-navy-mid">
            <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
          </h1>
          <p className="mt-1.5 text-center text-sm text-gray-500">Care together.</p>
          <p className="mt-4 text-center text-gray-600">
            Let's set up your family coordination
          </p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className={`h-2 w-16 rounded ${step >= 1 ? 'bg-primary-600' : 'bg-gray-300'}`} />
            <div className={`h-2 w-16 rounded ${step >= 2 ? 'bg-primary-600' : 'bg-gray-300'}`} />
            <div className={`h-2 w-16 rounded ${step >= 3 ? 'bg-primary-600' : 'bg-gray-300'}`} />
          </div>
        </div>

        {planError && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm font-medium text-amber-900">Circle limit reached</p>
            <p className="text-sm text-amber-800 mt-1">
              Your current plan includes one Circle. Upgrade to create additional Circles.
            </p>
            <Link to="/home" className="mt-3 inline-block text-sm font-medium text-primary-600 hover:text-primary-700">
              ← Back to Home
            </Link>
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleNext} className="mt-8 space-y-6">
            <div>
              <h2 className="text-xl font-medium text-gray-900 mb-4">Create your family</h2>
              <label htmlFor="familyName" className="label">
                Family name
              </label>
              <input
                id="familyName"
                name="familyName"
                type="text"
                required
                placeholder="e.g., Smith Family"
                className="input"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              />
              <p className="mt-2 text-sm text-gray-500">
                This helps identify who you're coordinating for
              </p>
            </div>

            <button type="submit" className="w-full btn btn-primary">
              Next
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleNext} className="mt-8 space-y-6">
            <div>
              <h2 className="text-xl font-medium text-gray-900 mb-4">What kind of family?</h2>
              <p className="text-sm text-gray-600 mb-4">
                Choose a starting point — you can always change these later.
              </p>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => applyPreset('elderly-care')}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedPreset === 'elderly-care'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Caring for an elderly relative</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Medications, emergency profile, appointments
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => applyPreset('co-parenting')}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedPreset === 'co-parenting'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Co-parenting</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Custody schedules, activities, handovers
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => applyPreset('family-children')}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedPreset === 'family-children'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Family with children</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Activities, schedules, coordination
                  </div>
                </button>
              </div>

              {selectedPreset && (
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Features enabled:</p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasMedications}
                      onChange={(e) => setHasMedications(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Medication tracking</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasCustody}
                      onChange={(e) => setHasCustody(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Custody calendar</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasActivities}
                      onChange={(e) => setHasActivities(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Scheduled activities</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasEmergency}
                      onChange={(e) => setHasEmergency(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Emergency profile</span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 btn btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!selectedPreset}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-medium text-gray-900">{dependentLabel}</h2>

              <div>
                <label htmlFor="dependentName" className="label">
                  Name *
                </label>
                <input
                  id="dependentName"
                  name="dependentName"
                  type="text"
                  required
                  className="input"
                  value={dependentName}
                  onChange={(e) => setDependentName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="dependentDob" className="label">
                  Date of birth (optional)
                </label>
                <input
                  id="dependentDob"
                  name="dependentDob"
                  type="date"
                  className="input"
                  value={dependentDob}
                  onChange={(e) => setDependentDob(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="dependentNotes" className="label">
                  Notes (optional)
                </label>
                <textarea
                  id="dependentNotes"
                  name="dependentNotes"
                  rows={3}
                  className="input"
                  placeholder="Any important details..."
                  value={dependentNotes}
                  onChange={(e) => setDependentNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 btn btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create family'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
